<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import type { DcfgFieldValue, DeviceActivityItem, DeviceSkuProfileChip, DeviceSummary, OtaCommandOption, OtaLogEntry } from '@ucc/shared';
  import {
    ApiError,
    clearOtaLog,
    fetchDeviceActivity,
    fetchDeviceConfig,
    fetchDeviceProfileOptions,
    fetchDevices,
    fetchOtaCommands,
    fetchOtaLog,
    issueDeviceConfigGet,
    issueOtaCommand,
    pingOtaDevice,
    updateDeviceConfig,
    updateOtaProfile,
    triggerFirmwareOta,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');

  let allDevices = $state<DeviceSummary[]>([]);
  let devicesLoading = $state(true);
  let search = $state('');
  let selected = $state<DeviceSummary | null>(null);

  let connectionType = $state<'mqtt' | 'mqtts' | 'https' | null>(null);
  let commands = $state<OtaCommandOption[]>([]);
  let commandsLoading = $state(false);
  let commandsError = $state<string | null>(null);
  let dcfgSupported = $state(false);

  let configFields = $state<DcfgFieldValue[] | null>(null);
  let configSource = $state<'get' | 'update' | null>(null);
  let configUpdatedAt = $state<string | null>(null);
  let configLoading = $state(false);
  let configBusy = $state<'get' | 'update' | null>(null);
  let configError = $state<string | null>(null);
  let editValues = $state<Record<string, string>>({});
  /** The last server record's `updatedAt` we've already shown — lets loadConfig() tell "nothing
   * new happened, keep my in-progress edit" apart from "a real new Get/Update result just landed,
   * show the truth" (see loadConfig()'s comment). */
  let configKnownUpdatedAt: string | null = null;

  let profilePushSupported = $state(false);
  let profileOptions = $state<DeviceSkuProfileChip[]>([]);
  let currentProfileId = $state<string | null>(null);
  let selectedProfileId = $state('');
  let profileOptionsLoading = $state(false);
  let profileBusy = $state(false);
  let profileError = $state<string | null>(null);

  let firmwareOtaSupported = $state(false);
  let firmwareBusy = $state(false);
  let firmwareError = $state<string | null>(null);

  const currentProfileLabel = $derived.by((): string => {
    const p = profileOptions.find((o) => o.id === currentProfileId);
    return p ? `${p.meterModel} v${p.profileVersion}` : '—';
  });

  let selectedCommandId = $state('');
  let issuing = $state(false);
  let issueError = $state<string | null>(null);
  let checkingOnline = $state(false);

  let logEntries = $state<OtaLogEntry[]>([]);
  let logLoading = $state(false);
  let logError = $state<string | null>(null);
  let clearing = $state(false);

  /** Raw device telemetry (Sampling/Alarm/PowerOn) — a completely separate table from
   * ota_commands, merged into the same Activity Log view below (2026-09-15, Pathma: staff
   * watching an OTA session on this page also needs to see the device's own PowerOn, not just
   * the OTA session's own trigger/apply/done rows). */
  let messageItems = $state<DeviceActivityItem[]>([]);

  type ActivityRow =
    | { rowKind: 'ota'; id: string; at: string; label: string; status: string; reply: string | null; by: string | null }
    | { rowKind: 'message'; id: string; at: string; label: string; detail: string | null; by: string | null };

  function messageLabel(m: DeviceActivityItem): string {
    if (m.readingKind === 'poweron') return 'PowerOn';
    if (m.readingKind === 'sample') return 'Sample';
    if (m.readingKind === 'alarm') return 'Alarm';
    return m.title;
  }

  const combinedActivity = $derived.by((): ActivityRow[] => {
    const rows: ActivityRow[] = [
      ...logEntries.map(
        (e): ActivityRow => ({ rowKind: 'ota', id: `ota:${e.id}`, at: e.createdAt, label: e.commandLabel, status: e.status, reply: e.replyLabel, by: e.actorEmail }),
      ),
      ...messageItems.map(
        (m): ActivityRow => ({ rowKind: 'message', id: m.id, at: m.at, label: messageLabel(m), detail: m.detail, by: m.source ?? m.direction ?? null }),
      ),
    ];
    return rows.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  });

  const ACTIVITY_PAGE_SIZE = 20;
  let activityPage = $state(1);
  const activityPageCount = $derived(Math.max(1, Math.ceil(combinedActivity.length / ACTIVITY_PAGE_SIZE)));
  const pagedActivity = $derived(combinedActivity.slice((activityPage - 1) * ACTIVITY_PAGE_SIZE, activityPage * ACTIVITY_PAGE_SIZE));

  /** Clamps back to the last valid page if the log shrinks (e.g. Clear log) while on a later page. */
  $effect(() => {
    if (activityPage > activityPageCount) activityPage = activityPageCount;
  });

  let jsonModalOpen = $state(false);
  let jsonModalTitle = $state('');
  let jsonModalContent = $state('');

  function openJsonModal(title: string, content: string) {
    jsonModalTitle = title;
    jsonModalContent = content;
    jsonModalOpen = true;
  }

  function closeJsonModal() {
    jsonModalOpen = false;
  }

  /** Pretty-prints when the detail is valid JSON (every decoded message is) — falls back to the raw text otherwise (e.g. an unrecognized/undecoded payload logged as hex). */
  function formatJsonModalContent(raw: string): string {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }

  /** Which action tab is showing — Activity Log is never a tab itself, it stays visible underneath
   * regardless, since it's what you watch while acting in whichever tab is active. */
  let activeTab = $state<'commands' | 'dcfg' | 'profiles' | 'firmware'>('commands');

  const availableTabs = $derived.by((): Array<{ id: 'commands' | 'dcfg' | 'profiles' | 'firmware'; label: string }> => {
    const tabs: Array<{ id: 'commands' | 'dcfg' | 'profiles' | 'firmware'; label: string }> = [
      { id: 'commands', label: 'Commands' },
    ];
    const mqttish = connectionType === 'mqtt' || connectionType === 'mqtts';
    if (mqttish && dcfgSupported) tabs.push({ id: 'dcfg', label: 'Device Configuration' });
    if (mqttish && profilePushSupported) tabs.push({ id: 'profiles', label: 'Operational Profiles' });
    if (mqttish && firmwareOtaSupported) tabs.push({ id: 'firmware', label: 'Firmware Update' });
    return tabs;
  });

  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const matches = $derived(
    search.trim().length === 0
      ? []
      : allDevices
          .filter((d) => d.uid.toLowerCase().includes(search.trim().toLowerCase()) || d.name.toLowerCase().includes(search.trim().toLowerCase()))
          .slice(0, 20),
  );

  /** MQTT/MQTTS liveness, derived from the most recent 'ping' row in the log — never a separate fetch. */
  const pingState = $derived.by((): 'unknown' | 'checking' | 'online' | 'offline' => {
    const pings = logEntries.filter((e) => e.kind === 'ping');
    const latest = pings[0]; // log is newest-first
    if (!latest) return 'unknown';
    if (latest.status === 'replied') return 'online';
    if (latest.status === 'timeout') return 'offline';
    return 'checking';
  });

  onMount(() => {
    void loadDevices();
    return () => stopPolling();
  });

  onDestroy(() => stopPolling());

  async function loadDevices() {
    devicesLoading = true;
    try {
      const res = await fetchDevices();
      allDevices = res.devices ?? [];
    } catch {
      allDevices = [];
    } finally {
      devicesLoading = false;
    }
  }

  function stopPolling() {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = null;
  }

  async function selectDevice(d: DeviceSummary) {
    stopPolling();
    selected = d;
    search = '';
    activeTab = 'commands';
    activityPage = 1;
    selectedCommandId = '';
    issueError = null;
    commandsError = null;
    logError = null;
    configError = null;
    configFields = null;
    configSource = null;
    configUpdatedAt = null;
    configKnownUpdatedAt = null;
    editValues = {};
    profilePushSupported = false;
    profileOptions = [];
    currentProfileId = null;
    selectedProfileId = '';
    profileError = null;
    firmwareOtaSupported = false;
    firmwareError = null;

    commandsLoading = true;
    try {
      const res = await fetchOtaCommands(d.id);
      connectionType = res.connectionType;
      commands = res.commands;
      dcfgSupported = res.dcfgSupported;
      profilePushSupported = res.profilePushSupported;
      firmwareOtaSupported = res.firmwareOtaSupported;
    } catch (err) {
      commandsError = err instanceof ApiError ? err.message : 'Failed to load commands';
      connectionType = null;
      commands = [];
      dcfgSupported = false;
      profilePushSupported = false;
      firmwareOtaSupported = false;
    } finally {
      commandsLoading = false;
    }

    if (dcfgSupported) await loadConfig();
    if (profilePushSupported) await loadProfileOptions();

    await refreshLog();
    pollTimer = setInterval(() => {
      void refreshLog();
      if (dcfgSupported) void loadConfig(true);
      if (profilePushSupported) void loadProfileOptions(true);
    }, 2500);
  }

  function clearSelection() {
    stopPolling();
    selected = null;
    connectionType = null;
    commands = [];
    logEntries = [];
    messageItems = [];
    configFields = null;
    configSource = null;
    configUpdatedAt = null;
    configKnownUpdatedAt = null;
    editValues = {};
    profilePushSupported = false;
    profileOptions = [];
    currentProfileId = null;
    selectedProfileId = '';
    profileError = null;
    firmwareOtaSupported = false;
    firmwareError = null;
  }

  async function refreshLog() {
    if (!selected) return;
    logLoading = true;
    try {
      const res = await fetchOtaLog(selected.id);
      logEntries = res.entries;
      logError = null;
    } catch (err) {
      logError = err instanceof ApiError ? err.message : 'Failed to load activity log';
    } finally {
      logLoading = false;
    }
    await loadMessages();
  }

  /** Never surfaces its own error banner — Activity Log already has one (from the OTA side), and a
   * transient failure here just means messages are briefly stale, not that the page is broken. */
  async function loadMessages() {
    if (!selected) return;
    try {
      const res = await fetchDeviceActivity(selected.id);
      messageItems = res.items.filter((i) => i.kind === 'message');
    } catch {
      /* leave last-known messages on a transient failure */
    }
  }

  async function checkOnline() {
    if (!selected) return;
    checkingOnline = true;
    try {
      await pingOtaDevice(selected.id);
      await refreshLog();
    } catch (err) {
      issueError = err instanceof ApiError ? err.message : 'Ping failed';
    } finally {
      checkingOnline = false;
    }
  }

  async function onIssue(e: Event) {
    e.preventDefault();
    if (!selected || !selectedCommandId) return;
    issueError = null;
    issuing = true;
    try {
      await issueOtaCommand(selected.id, selectedCommandId);
      await refreshLog();
    } catch (err) {
      issueError = err instanceof ApiError ? err.message : 'Issue failed';
    } finally {
      issuing = false;
    }
  }

  const SLAVE_ID_RE = /^slaveId([1-5])$/;

  /** Falls back to showing everything if Meter Count isn't loaded/parseable yet. */
  function currentMeterCount(): number {
    const n = Number(editValues['meterCount']);
    return Number.isFinite(n) && n > 0 ? n : 5;
  }

  /** Slave ID boxes beyond the current Meter Count are hidden in the template (see isSlaveIdHidden) — this zeroes their value too, since firmware rejects a non-zero slot beyond Meter Count even on an unrelated field edit. */
  function enforceMeterCount() {
    if (!configFields) return;
    const count = currentMeterCount();
    for (const f of configFields) {
      const m = SLAVE_ID_RE.exec(f.id);
      if (m && Number(m[1]) > count) editValues[f.id] = '0';
    }
  }

  function isSlaveIdHidden(fieldId: string): boolean {
    const m = SLAVE_ID_RE.exec(fieldId);
    return m ? Number(m[1]) > currentMeterCount() : false;
  }

  /**
   * `silent` is true for the background poll refresh — it must never touch `configLoading`, in
   * either direction, or the card flickers between "Loading…" and "No configuration on file yet"
   * every 2.5s while waiting for the first Get.
   *
   * editValues is only reseeded when the fetched record's `updatedAt` is actually new (a Get or
   * Update just resolved server-side) — not on every call. That's what tells "staff is mid-typing,
   * nothing new happened yet, don't clobber it" apart from "a real new value just arrived, show the
   * truth" (e.g. clicking "Refresh from device" after editing a field, or another actor's change) —
   * comparing against the *record id* is what used to be missing: reseeding only when a field's own
   * key was absent from editValues meant any field, once touched, could never show a newer value
   * again for the rest of the page session, silently breaking "Refresh from device" for it.
   */
  async function loadConfig(silent = false) {
    if (!selected) return;
    if (!silent) configLoading = true;
    try {
      const res = await fetchDeviceConfig(selected.id);
      const newUpdatedAt = res.config?.updatedAt ?? null;
      const isNewRecord = newUpdatedAt !== configKnownUpdatedAt;
      configFields = res.config?.fields ?? null;
      configSource = res.config?.source ?? null;
      configUpdatedAt = newUpdatedAt;
      if (configFields && isNewRecord) {
        for (const f of configFields) {
          if (f.editable) editValues[f.id] = f.value;
        }
        enforceMeterCount();
        configKnownUpdatedAt = newUpdatedAt;
      }
      configError = null;
    } catch (err) {
      configError = err instanceof ApiError ? err.message : 'Failed to load configuration';
    } finally {
      if (!silent) configLoading = false;
    }
  }

  async function getConfig() {
    if (!selected) return;
    configError = null;
    configBusy = 'get';
    try {
      await issueDeviceConfigGet(selected.id);
      await refreshLog();
    } catch (err) {
      configError = err instanceof ApiError ? err.message : 'Failed to send';
    } finally {
      configBusy = null;
    }
  }

  async function submitConfigUpdate() {
    if (!selected) return;
    configError = null;
    configBusy = 'update';
    try {
      await updateDeviceConfig(selected.id, editValues);
      await refreshLog();
    } catch (err) {
      configError = err instanceof ApiError ? err.message : 'Failed to send';
    } finally {
      configBusy = null;
    }
  }

  /**
   * `silent` mirrors loadConfig()'s poll behavior. selectedProfileId is only ever seeded once, when
   * empty — after that it's entirely staff-driven, so a background poll refreshing currentProfileId
   * (e.g. once a push confirms) never clobbers a candidate the staff already picked.
   */
  async function loadProfileOptions(silent = false) {
    if (!selected) return;
    if (!silent) profileOptionsLoading = true;
    try {
      const res = await fetchDeviceProfileOptions(selected.id);
      profileOptions = res.options;
      currentProfileId = res.currentProfileId;
      if (!selectedProfileId && currentProfileId) selectedProfileId = currentProfileId;
      profileError = null;
    } catch (err) {
      profileError = err instanceof ApiError ? err.message : 'Failed to load profile options';
    } finally {
      if (!silent) profileOptionsLoading = false;
    }
  }

  async function submitProfileUpdate() {
    if (!selected || !selectedProfileId) return;
    profileError = null;
    profileBusy = true;
    try {
      await updateOtaProfile(selected.id, { profileRowId: selectedProfileId });
      await refreshLog();
    } catch (err) {
      profileError = err instanceof ApiError ? err.message : 'Failed to send';
    } finally {
      profileBusy = false;
    }
  }

  async function submitFirmwareUpdate() {
    if (!selected) return;
    firmwareError = null;
    firmwareBusy = true;
    try {
      await triggerFirmwareOta(selected.id);
      await refreshLog();
    } catch (err) {
      firmwareError = err instanceof ApiError ? err.message : 'Failed to send';
    } finally {
      firmwareBusy = false;
    }
  }

  async function onClearLog() {
    if (!selected) return;
    if (!confirm(`Permanently delete all OTA history for ${selected.uid}? This cannot be undone.`)) return;
    clearing = true;
    try {
      await clearOtaLog(selected.id);
      await refreshLog();
    } catch (err) {
      logError = err instanceof ApiError ? err.message : 'Clear failed';
    } finally {
      clearing = false;
    }
  }

  let exporting = $state(false);

  /** Lazy-loaded — xlsx is a sizable library, no reason to ship it to every visitor up front. */
  async function exportXlsx() {
    if (!selected || logEntries.length === 0) return;
    exporting = true;
    try {
      const XLSX = await import('xlsx');
      const rows = logEntries.map((e) => ({
        When: e.createdAt,
        Kind: e.kind === 'ping' ? 'Connectivity check' : 'Command',
        Command: e.commandLabel,
        Status: e.status,
        Reply: e.replyLabel ?? '',
        'Sent At': e.sentAt ?? '',
        'Replied At': e.repliedAt ?? '',
        'Issued By': e.actorEmail ?? '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'OTA Log');
      XLSX.writeFile(wb, `ota-log-${selected.uid}.xlsx`);
    } finally {
      exporting = false;
    }
  }

  function statusTone(status: string): 'success' | 'neutral' | 'warning' | 'danger' {
    if (status === 'replied') return 'success';
    if (status === 'sent' || status === 'queued') return 'warning';
    if (status === 'timeout' || status === 'superseded') return 'danger';
    return 'neutral';
  }

  function fmt(ts: string | null): string {
    return ts ? new Date(ts).toLocaleString() : '—';
  }
</script>

<div class="page">
  <h2>OTA</h2>
  <p class="sub">
    Send a command to a device and watch its reply. Staff-only, independent of claim/ownership — any
    device in inventory can be commanded, as long as its SKU has a Command Set attached (see
    <a href="#/settings/command-sets">Command Sets</a>).
  </p>

  {#if !isStaff}
    <Card title="Staff only">
      <p class="muted">Only admin/staff can use OTA.</p>
    </Card>
  {:else}
    <Card title="Device" subtitle="Search by UID or name">
      <div class="picker">
        <input
          class="input"
          type="text"
          placeholder={devicesLoading ? 'Loading devices…' : 'e.g. 08AA0001'}
          bind:value={search}
          disabled={devicesLoading}
        />
        {#if matches.length > 0}
          <ul class="matches">
            {#each matches as d (d.id)}
              <li>
                <button type="button" class="match-row" onclick={() => void selectDevice(d)}>
                  <span class="mono">{d.uid}</span>
                  <span class="muted small">{d.name}{d.sku ? ` · ${d.sku}` : ''}</span>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>

      {#if selected}
        <div class="selected-row">
          <div>
            <span class="mono selected-uid">{selected.uid}</span>
            <span class="muted small">{selected.name}</span>
          </div>
          <button type="button" class="linkish" onclick={clearSelection}>Change device</button>
        </div>

        {#if commandsLoading}
          <p class="muted">Loading…</p>
        {:else if commandsError}
          <p class="err" role="alert">{commandsError}</p>
        {:else if !connectionType}
          <p class="muted banner">This device is not assigned to a Device Pool — OTA is unavailable.</p>
        {:else}
          <!-- Persistent, one place, applies to every tab below — was previously duplicated as a
               "Check Online first" hint inside every single card. -->
          {#if connectionType === 'mqtt' || connectionType === 'mqtts'}
            <div class="online-row">
              <span class="muted small">Connection: {connectionType.toUpperCase()}</span>
              <span class="state-badge" data-tone={pingState === 'online' ? 'success' : pingState === 'offline' ? 'danger' : pingState === 'checking' ? 'warning' : 'neutral'}>
                {pingState === 'unknown' ? 'Not checked' : pingState}
              </span>
              <Button variant="ghost" size="sm" disabled={checkingOnline} onclick={() => void checkOnline()}>
                {checkingOnline ? 'Checking…' : 'Check Online'}
              </Button>
              {#if pingState !== 'online'}
                <span class="muted small">Check Online first — every action below is disabled until the device confirms it's reachable.</span>
              {/if}
            </div>
          {:else}
            <p class="muted small">HTTPS device — last seen: {fmt(selected.lastSeenAt)}. Commands queue and deliver on the device's next check-in.</p>
          {/if}
        {/if}
      {/if}
    </Card>

    {#if selected && connectionType}
        <div class="tabs" role="tablist">
          {#each availableTabs as t (t.id)}
            <button type="button" class:active={activeTab === t.id} onclick={() => (activeTab = t.id)}>{t.label}</button>
          {/each}
        </div>

        {#if activeTab === 'commands'}
        <Card title="Commands">
          {#if commands.length === 0 && !dcfgSupported}
            <p class="muted banner">
              No Command Set attached to this device's SKU — nothing to send. Attach one in
              <a href="#/settings/skus">SKU Manager</a>.
            </p>
          {:else if commands.length > 0}
            <form class="form" onsubmit={(e) => void onIssue(e)}>
              <label class="field">
                <span class="label">Command</span>
                <select class="input" bind:value={selectedCommandId} disabled={issuing}>
                  <option value="">Choose a command…</option>
                  {#each commands as c (c.id)}
                    <option value={c.id}>{c.label}</option>
                  {/each}
                </select>
              </label>
              {#if issueError}
                <p class="err" role="alert">{issueError}</p>
              {/if}
              <div class="actions">
                <Button
                  type="submit"
                  disabled={issuing ||
                    !selectedCommandId ||
                    ((connectionType === 'mqtt' || connectionType === 'mqtts') && pingState !== 'online')}
                >
                  {issuing ? 'Issuing…' : connectionType === 'https' ? 'Queue command' : 'Send command'}
                </Button>
              </div>
            </form>
          {/if}
        </Card>
        {:else if activeTab === 'dcfg'}
          <Card title="Device Configuration" subtitle="Read or change the device's sampling/topology parameters">
            {#if configLoading && !configFields}
              <p class="muted">Loading…</p>
            {:else if !configFields}
              <p class="muted banner">No configuration on file yet.</p>
            {:else}
              <p class="muted small">
                Last {configSource === 'update' ? 'confirmed applied' : 'read from device'}: {fmt(configUpdatedAt)}
              </p>
              <div class="config-grid">
                {#each configFields as f (f.id)}
                  {#if !isSlaveIdHidden(f.id)}
                    <label class="field">
                      <span class="label">{f.label}</span>
                      {#if f.editable && f.min != null && f.max != null}
                        <select class="input" bind:value={editValues[f.id]} disabled={configBusy !== null} onchange={enforceMeterCount}>
                          {#each Array.from({ length: f.max - f.min + 1 }, (_, i) => f.min! + i) as n (n)}
                            <option value={String(n)}>{n}</option>
                          {/each}
                        </select>
                      {:else if f.editable}
                        <input class="input" type="text" bind:value={editValues[f.id]} disabled={configBusy !== null} />
                      {:else}
                        <span class="mono readonly-value">{f.value}</span>
                      {/if}
                    </label>
                  {/if}
                {/each}
              </div>
            {/if}

            {#if configError}
              <p class="err" role="alert">{configError}</p>
            {/if}

            <div class="actions">
              <Button variant="ghost" disabled={pingState !== 'online' || configBusy !== null} onclick={() => void getConfig()}>
                {configBusy === 'get' ? 'Sending…' : configFields ? 'Refresh from device' : 'Get Current Configuration'}
              </Button>
              {#if configFields}
                <Button disabled={pingState !== 'online' || configBusy !== null} onclick={() => void submitConfigUpdate()}>
                  {configBusy === 'update' ? 'Sending…' : 'Update Device'}
                </Button>
              {/if}
            </div>
          </Card>
        {:else if activeTab === 'profiles'}
          <Card title="Operational Profiles" subtitle="Push the RS485 (meter) and Alarm profiles the device runs">
            {#if profileOptionsLoading}
              <p class="muted">Loading…</p>
            {:else if profileOptions.length === 0}
              <p class="muted banner">No profiles linked to this device's SKU.</p>
            {:else}
              <p class="muted small">Currently active on device: {currentProfileLabel}</p>
              <label class="field">
                <span class="label">Profile</span>
                <select class="input" bind:value={selectedProfileId} disabled={profileBusy}>
                  {#each profileOptions as p (p.id)}
                    <option value={p.id}>{p.meterModel} v{p.profileVersion}{p.isDefault ? ' (SKU default)' : ''}</option>
                  {/each}
                </select>
              </label>
            {/if}

            {#if profileError}
              <p class="err" role="alert">{profileError}</p>
            {/if}

            <div class="actions">
              <Button
                disabled={pingState !== 'online' || profileBusy || !selectedProfileId || profileOptions.length === 0}
                onclick={() => void submitProfileUpdate()}
              >
                {profileBusy ? 'Sending…' : 'Update Device'}
              </Button>
            </div>
            <p class="muted small">
              Runs RS485 first, then Alarm. Watch the Activity Log below for progress — the device's
              active profile only updates here once both are confirmed applied.
            </p>
          </Card>
        {:else if activeTab === 'firmware'}
          <Card title="Firmware Update" subtitle="Push the device's SKU firmware over MQTT + HTTPS">
            <p class="muted small">
              Sends the currently uploaded <span class="mono">.bin</span> for this device's SKU (manage it in
              <a href="#/settings/skus">SKU Manager</a>) — the device downloads it itself over HTTPS once
              triggered.
            </p>
            {#if firmwareError}
              <p class="err" role="alert">{firmwareError}</p>
            {/if}
            <div class="actions">
              <Button disabled={pingState !== 'online' || firmwareBusy} onclick={() => void submitFirmwareUpdate()}>
                {firmwareBusy ? 'Sending…' : 'Update Firmware'}
              </Button>
            </div>
            <p class="muted small">
              Watch the Activity Log below for progress — trigger, device accepting, downloading, and the
              final applied/rejected result all show there.
            </p>
          </Card>
        {/if}

        <Card title="Activity log" subtitle="Latest first — OTA sessions and the device's own raw messages (Sampling/Alarm/PowerOn)">
          {#snippet actions()}
            <Button variant="ghost" size="sm" disabled={exporting || logEntries.length === 0} onclick={() => void exportXlsx()}>
              {exporting ? 'Preparing…' : 'Download XLSX (OTA only)'}
            </Button>
            <Button variant="danger" size="sm" disabled={clearing || logEntries.length === 0} onclick={() => void onClearLog()}>
              {clearing ? 'Clearing…' : 'Clear log'}
            </Button>
          {/snippet}
          {#if logError}
            <p class="err" role="alert">{logError}</p>
          {:else if logLoading && combinedActivity.length === 0}
            <p class="muted">Loading…</p>
          {:else if combinedActivity.length === 0}
            <p class="muted banner">No activity yet.</p>
          {:else}
            <div class="table-wrap">
              <table class="table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Kind</th>
                    <th>Status</th>
                    <th>Reply / Detail</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {#each pagedActivity as row (row.id)}
                    <tr>
                      <td class="mono small">{fmt(row.at)}</td>
                      <td>{row.label}</td>
                      {#if row.rowKind === 'ota'}
                        <td><span class="state-badge" data-tone={statusTone(row.status)}>{row.status}</span></td>
                        <td>{row.reply ?? '—'}</td>
                      {:else}
                        <td><span class="state-badge">message</span></td>
                        <td>
                          {#if row.detail}
                            <button type="button" class="linkish" onclick={() => openJsonModal(`${row.label} · ${fmt(row.at)}`, formatJsonModalContent(row.detail ?? ''))}
                              >View JSON</button
                            >
                          {:else}
                            —
                          {/if}
                        </td>
                      {/if}
                      <td class="small">{row.by ?? '—'}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
            {#if activityPageCount > 1}
              <div class="pager">
                <Button variant="ghost" size="sm" disabled={activityPage <= 1} onclick={() => (activityPage -= 1)}>← Prev</Button>
                <span class="muted small">Page {activityPage} of {activityPageCount} ({combinedActivity.length} total)</span>
                <Button variant="ghost" size="sm" disabled={activityPage >= activityPageCount} onclick={() => (activityPage += 1)}>Next →</Button>
              </div>
            {/if}
          {/if}
        </Card>
    {/if}
  {/if}
</div>

{#if jsonModalOpen}
  <div class="modal-backdrop" role="presentation" onclick={closeJsonModal}>
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-label="Activity log detail"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => {
        if (e.key === 'Escape') closeJsonModal();
      }}
    >
      <header class="modal-head">
        <h3>{jsonModalTitle}</h3>
        <button class="modal-close" onclick={closeJsonModal} aria-label="Close">×</button>
      </header>
      <div class="modal-body">
        <pre class="modal-json">{jsonModalContent}</pre>
      </div>
    </div>
  </div>
{/if}

<style>
  .page {
    max-width: 64rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .sub {
    margin: 0.35rem 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
  }
  .sub a {
    color: var(--accent, #3b82f6);
  }
  .picker {
    position: relative;
  }
  .input {
    font: inherit;
    width: 100%;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
    box-sizing: border-box;
  }
  .matches {
    list-style: none;
    margin: 0.35rem 0 0;
    padding: 0;
    border: 1px solid var(--border, #2a2a32);
    border-radius: 0.5rem;
    overflow: hidden;
  }
  .match-row {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    align-items: flex-start;
    background: none;
    border: none;
    padding: 0.5rem 0.65rem;
    cursor: pointer;
    color: inherit;
    font: inherit;
    text-align: left;
  }
  .match-row:hover {
    background: var(--surface-2, #1a1a22);
  }
  .selected-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--border, #2a2a32);
  }
  .selected-uid {
    font-weight: 600;
    margin-right: 0.5rem;
  }
  .muted {
    color: var(--text-muted);
  }
  .small {
    font-size: 0.8rem;
  }
  .banner {
    padding: 1rem 0;
    margin: 0;
  }
  .err {
    color: #f87171;
    margin: 0;
    font-size: 0.9rem;
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }
  .online-row {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
  }
  .tabs {
    display: flex;
    gap: 0.25rem;
    border-bottom: 1px solid var(--border, #2a2a32);
  }
  .tabs button {
    font: inherit;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 0.55rem 0.85rem;
    color: var(--text-muted);
    cursor: pointer;
  }
  .tabs button:hover {
    color: var(--text);
  }
  .tabs button.active {
    color: var(--text);
    border-bottom-color: var(--accent, #3b82f6);
    font-weight: 500;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .label {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .actions {
    display: flex;
    gap: 0.75rem;
  }
  .linkish {
    background: none;
    border: none;
    color: var(--accent, #3b82f6);
    cursor: pointer;
    font: inherit;
    padding: 0.15rem 0.35rem;
    font-size: 0.85rem;
  }
  .table-wrap {
    overflow-x: auto;
  }
  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.55rem 0.4rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: top;
  }
  th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.8rem;
  }
  .state-badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: var(--surface-2, #eee);
    color: var(--text-muted);
    white-space: nowrap;
    text-transform: capitalize;
  }
  .state-badge[data-tone='success'] {
    background: color-mix(in srgb, var(--success, #22c55e) 18%, transparent);
    color: var(--success, #22c55e);
  }
  .state-badge[data-tone='warning'] {
    background: color-mix(in srgb, #f59e0b 18%, transparent);
    color: #f59e0b;
  }
  .state-badge[data-tone='danger'] {
    background: color-mix(in srgb, #f87171 18%, transparent);
    color: #f87171;
  }
  .config-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
    gap: 0.65rem;
    margin: 0.5rem 0;
  }
  .readonly-value {
    padding: 0.5rem 0.65rem;
    color: var(--text-muted);
  }
  .pager {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-top: 0.75rem;
  }
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: color-mix(in srgb, black 55%, transparent);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    padding: 1.5rem;
  }
  .modal {
    background: var(--surface, #fff);
    border-radius: 0.75rem;
    border: 1px solid var(--border);
    width: min(48rem, 100%);
    height: min(32rem, 100%);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  }
  .modal-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--border);
  }
  .modal-head h3 {
    margin: 0;
    font-size: 1rem;
  }
  .modal-close {
    font: inherit;
    font-size: 1.3rem;
    line-height: 1;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 0.2rem 0.4rem;
  }
  .modal-close:hover {
    color: var(--text);
  }
  .modal-body {
    flex: 1;
    overflow: auto;
    padding: 1rem 1.25rem;
  }
  .modal-json {
    margin: 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.8rem;
    white-space: pre-wrap;
    word-break: break-word;
    color: var(--text);
  }
</style>
